import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  FileText,
  Calendar,
  Sparkles,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
} from "lucide-react";
import { API_ENDPOINTS } from "../config/api";
import apiRequest from "../utils/api";
import authUtils from "../utils/auth";
import { EventSourcePolyfill, NativeEventSource } from "event-source-polyfill";

interface StudentRecord {
  id: string;
  uploadType: number;
  bucketKey: string;
  documentNumber: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AnalysisComponentResult {
  componentType: string;
  status: string;
  analysisData: any;
  pdfKey: string;
  errorMessage?: string;
  processingTimeMs: number;
}

interface AnalysisStatus {
  sessionId: string | null;
  status: "idle" | "processing" | "completed" | "failed";
  message?: string;
  completedAt: string | null;
  analysisResults?: Record<string, AnalysisComponentResult>;
}

interface RecordWithAnalysis extends StudentRecord {
  analysis?: AnalysisStatus;
}

interface AnalysisEventData {
  sessionId?: string;
  status?: "processing" | "completed" | "failed";
  message?: string;
  completedAt?: string | null;
  analysisResults?: Record<string, AnalysisComponentResult>;
}

export const StudentRecordDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<RecordWithAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzingRecords, setAnalyzingRecords] = useState<Set<string>>(
    new Set()
  );
  const [sseConnections, setSseConnections] = useState<
    Map<string, EventSource | AbortController>
  >(new Map());
  const sseConnectionsRef = useRef<Map<string, EventSource | AbortController>>(
    new Map()
  );

  // SSE 연결 상태 동기화
  useEffect(() => {
    sseConnectionsRef.current = sseConnections;
  }, [sseConnections]);

  // 컴포넌트 언마운트 시 SSE 연결 정리
  useEffect(() => {
    const sseConnections = sseConnectionsRef.current;

    return () => {
      sseConnections.forEach((connection) => {
        if (connection instanceof EventSource) {
          connection.close();
        } else {
          connection.abort();
        }
      });
      sseConnections.clear();
    };
  }, []);

  // SSE 연결을 통한 분석 상태 스트림 수신 (event-source-polyfill 사용 - Authorization 헤더 지원)
  const connectToStatusStream = useCallback(
    (recordId: string, sessionId: string) => {
      // 기존 연결이 있다면 종료 (ref를 통해 안전하게 접근)
      const existingConnection = sseConnectionsRef.current.get(sessionId);
      if (existingConnection) {
        if (existingConnection instanceof EventSource) {
          existingConnection.close();
        } else {
          existingConnection.abort();
        }
        sseConnectionsRef.current.delete(sessionId);
      }

      // 이미 연결 중인지 확인 (중복 방지)
      if (sseConnectionsRef.current.has(sessionId)) {
        console.log(`SSE 연결이 이미 진행 중입니다: ${sessionId}`);
        return;
      }

      const accessToken = authUtils.getAccessToken();
      let streamUrl = API_ENDPOINTS.INTERVIEW.STATUS_STREAM(sessionId);

      // URL에서 줄바꿈 및 공백 제거 (안전 장치)
      streamUrl = streamUrl.trim().replace(/\n/g, "").replace(/\r/g, "");

      console.log(`🔗 SSE 연결 시도: ${streamUrl}`);
      console.log(`📋 Session ID: ${sessionId}`);

      // event-source-polyfill을 사용하여 EventSource 생성 (Authorization 헤더 지원)
      const EventSourceClass = EventSourcePolyfill || NativeEventSource;

      const eventSource = new EventSourceClass(streamUrl, {
        headers: {
          Authorization: accessToken ? `Bearer ${accessToken}` : "",
        },
        withCredentials: true,
      });

      // EventSource 저장 (타입 단언 사용)
      sseConnectionsRef.current.set(sessionId, eventSource as EventSource);
      setSseConnections(new Map(sseConnectionsRef.current));

      let eventCount = 0;

      // 연결 열림
      eventSource.onopen = () => {
        console.log(`✅ EventSource 연결 성공: ${sessionId}`);
      };

      // status-update 이벤트 수신
      eventSource.addEventListener("status-update", ((event: MessageEvent) => {
        try {
          eventCount++;
          console.log(`📦 SSE 이벤트 수신 (${sessionId}):`, event.data);

          const rawData = JSON.parse(event.data);
          let eventData: AnalysisEventData;
          if (rawData.success && rawData.data) {
            eventData = rawData.data;
          } else {
            eventData = rawData;
          }

          console.log(`✅ SSE 이벤트 파싱 성공 (${sessionId}):`, eventData);

          // 레코드 상태 업데이트
          setRecord((prev) =>
            prev
              ? {
                  ...prev,
                  analysis: {
                    sessionId: eventData.sessionId || sessionId,
                    status:
                      eventData.status === "processing"
                        ? "processing"
                        : eventData.status === "completed"
                        ? "completed"
                        : eventData.status === "failed"
                        ? "failed"
                        : "processing",
                    message: eventData.message,
                    completedAt: eventData.completedAt || null,
                    analysisResults: eventData.analysisResults,
                  },
                }
              : null
          );

          // 완료 또는 실패 시 연결 종료
          const status = eventData.status || "processing";
          if (status === "completed" || status === "failed") {
            eventSource.close();
            sseConnectionsRef.current.delete(sessionId);
            setSseConnections(new Map(sseConnectionsRef.current));
            console.log(
              `✅ SSE 연결 종료 (${sessionId}): 총 ${eventCount}개 이벤트 수신`
            );
          }
        } catch (error) {
          console.error("SSE 이벤트 파싱 실패:", error);
        }
      }) as EventListener);

      // 기본 message 이벤트 (이벤트 타입이 없을 때)
      eventSource.onmessage = (event: MessageEvent) => {
        try {
          eventCount++;
          console.log(`📦 SSE 기본 이벤트 수신 (${sessionId}):`, event.data);

          const rawData = JSON.parse(event.data);
          let eventData: AnalysisEventData;
          if (rawData.success && rawData.data) {
            eventData = rawData.data;
          } else {
            eventData = rawData;
          }

          // 레코드 상태 업데이트
          setRecord((prev) =>
            prev
              ? {
                  ...prev,
                  analysis: {
                    sessionId: eventData.sessionId || sessionId,
                    status:
                      eventData.status === "processing"
                        ? "processing"
                        : eventData.status === "completed"
                        ? "completed"
                        : eventData.status === "failed"
                        ? "failed"
                        : "processing",
                    message: eventData.message,
                    completedAt: eventData.completedAt || null,
                    analysisResults: eventData.analysisResults,
                  },
                }
              : null
          );

          const status = eventData.status || "processing";
          if (status === "completed" || status === "failed") {
            eventSource.close();
            sseConnectionsRef.current.delete(sessionId);
            setSseConnections(new Map(sseConnectionsRef.current));
          }
        } catch (error) {
          console.error("SSE 이벤트 파싱 실패:", error);
        }
      };

      // 에러 처리
      eventSource.onerror = (error: Event) => {
        console.error(`EventSource 오류 (${sessionId}):`, error);

        // 연결이 종료된 경우
        if (eventSource.readyState === EventSource.CLOSED) {
          console.log(`EventSource 연결 종료됨: ${sessionId}`);

          setRecord((prev) =>
            prev
              ? {
                  ...prev,
                  analysis: {
                    ...prev.analysis!,
                    status: "failed",
                    message: "SSE 연결이 종료되었습니다.",
                  },
                }
              : null
          );

          sseConnectionsRef.current.delete(sessionId);
          setSseConnections(new Map(sseConnectionsRef.current));
        }
      };
    },
    [] // 의존성 배열을 비워서 무한 루프 방지
  );

  // 분석 결과 조회
  const fetchAnalysisResult = useCallback(
    async (studentRecordId: string) => {
      try {
        const response = await apiRequest(
          API_ENDPOINTS.INTERVIEW.GET_RESULT(studentRecordId),
          {
            method: "GET",
          }
        );

        if (response.ok) {
          const responseData = await response.json();
          if (responseData.success && responseData.data) {
            const analysisData = responseData.data;

            // 레코드에 분석 결과 업데이트
            setRecord((prev) =>
              prev
                ? {
                    ...prev,
                    analysis: {
                      sessionId: analysisData.sessionId || null,
                      status:
                        analysisData.status === "processing"
                          ? "processing"
                          : analysisData.status === "completed"
                          ? "completed"
                          : analysisData.status === "failed"
                          ? "failed"
                          : "idle",
                      message: analysisData.message || responseData.message,
                      completedAt: analysisData.completedAt || null,
                      analysisResults: analysisData.analysisResults,
                    },
                  }
                : null
            );

            // 분석이 처리 중이면 SSE 연결 시작
            if (
              analysisData.status === "processing" &&
              analysisData.sessionId
            ) {
              connectToStatusStream(studentRecordId, analysisData.sessionId);
            }
          }
        } else {
          // 404는 분석이 아직 시작되지 않은 것으로 간주
          if (response.status === 404) {
            console.log("분석 결과가 아직 없습니다.");
          } else {
            // 500 등의 서버 에러는 로그만 남기고 계속 진행
            console.warn(
              `분석 결과 조회 실패: ${response.status} ${
                response.statusText || ""
              }`
            );
          }
        }
      } catch (error) {
        // 네트워크 오류는 무시 (분석이 아직 시작되지 않았을 수 있음)
        console.log("분석 결과 조회 실패 (무시됨):", error);
      }
    },
    [connectToStatusStream]
  );

  const fetchStudentRecord = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiRequest(API_ENDPOINTS.STUDENT_RECORDS, {
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const foundRecord = data.data.find((r: StudentRecord) => r.id === id);
          if (foundRecord) {
            setRecord(foundRecord);
            // 파일 정보 로드 후 분석 결과도 조회
            fetchAnalysisResult(foundRecord.id);
          }
        }
      }
    } catch (error) {
      console.error("학생부 파일 상세 조회 실패:", error);
    } finally {
      setLoading(false);
    }
  }, [id, fetchAnalysisResult]);

  // 학생부 파일 상세 조회
  useEffect(() => {
    if (id) {
      fetchStudentRecord();
    }
  }, [id, fetchStudentRecord]);

  const getUploadTypeLabel = (uploadType: number) => {
    const types: Record<number, string> = {
      1: "일반",
      2: "대량",
      3: "개별",
    };
    return types[uploadType] || `타입 ${uploadType}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getFileNameFromBucketKey = (bucketKey: string) => {
    const parts = bucketKey.split("/");
    return parts[parts.length - 1] || bucketKey;
  };

  // 면접 분석 요청
  const requestAnalysis = async (recordId: string) => {
    try {
      setAnalyzingRecords((prev) => new Set(prev).add(recordId));

      const response = await apiRequest(API_ENDPOINTS.INTERVIEW.ANALYZE, {
        method: "POST",
        body: JSON.stringify({
          studentRecordId: recordId,
          sessionId: null,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();

        if (responseData.success && responseData.data) {
          const analysisData = responseData.data;

          // 레코드 업데이트
          setRecord((prev) =>
            prev
              ? {
                  ...prev,
                  analysis: {
                    sessionId: analysisData.sessionId,
                    status:
                      analysisData.status === "processing"
                        ? "processing"
                        : analysisData.status === "completed"
                        ? "completed"
                        : analysisData.status === "failed"
                        ? "failed"
                        : "idle",
                    message: analysisData.message || responseData.message,
                    completedAt: analysisData.completedAt || null,
                    analysisResults: analysisData.analysisResults,
                  },
                }
              : null
          );

          // 분석이 처리 중이면 SSE 연결 시작
          if (analysisData.status === "processing" && analysisData.sessionId) {
            connectToStatusStream(recordId, analysisData.sessionId);
          }
        } else {
          alert(
            `분석 요청 실패: ${
              responseData.message || "알 수 없는 오류가 발생했습니다."
            }`
          );
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(
          `분석 요청 실패: ${
            errorData.message || "알 수 없는 오류가 발생했습니다."
          }`
        );
      }
    } catch (error) {
      console.error("면접 분석 요청 실패:", error);
      alert("면접 분석 요청 중 오류가 발생했습니다.");
    } finally {
      setAnalyzingRecords((prev) => {
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
      case "success":
        return (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-700 border-green-300"
          >
            <CheckCircle2 className="h-3 w-3 mr-1" />
            완료
          </Badge>
        );
      case "failed":
      case "error":
        return (
          <Badge
            variant="outline"
            className="bg-red-50 text-red-700 border-red-300"
          >
            <XCircle className="h-3 w-3 mr-1" />
            실패
          </Badge>
        );
      case "processing":
        return (
          <Badge
            variant="outline"
            className="bg-yellow-50 text-yellow-700 border-yellow-300"
          >
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            처리 중
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            <Clock className="h-3 w-3 mr-1" />
            대기
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">로딩 중...</div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">파일을 찾을 수 없습니다.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/student-records")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            목록으로
          </Button>
          <div className="flex items-center space-x-2">
            <FileText className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">
              {getFileNameFromBucketKey(record.bucketKey)}
            </h1>
          </div>
        </div>
        <Button
          onClick={() => requestAnalysis(record.id)}
          disabled={
            analyzingRecords.has(record.id) ||
            record.analysis?.status === "processing"
          }
          className="flex items-center space-x-2"
        >
          {analyzingRecords.has(record.id) ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>요청 중...</span>
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              <span>분석 요청</span>
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 파일 정보 */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>파일 정보</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    파일 ID
                  </p>
                  <p className="text-sm">{record.id}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    업로드 타입
                  </p>
                  <Badge variant="secondary">
                    {getUploadTypeLabel(record.uploadType)}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    문서번호
                  </p>
                  <p className="text-sm">{record.documentNumber || "-"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Bucket Key
                  </p>
                  <p className="text-sm break-all">{record.bucketKey}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    생성일
                  </p>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm">{formatDate(record.createdAt)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    수정일
                  </p>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm">{formatDate(record.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 분석 결과 */}
          {record.analysis && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>면접 분석 결과</CardTitle>
                  {record.analysis.status === "processing" && (
                    <Badge
                      variant="outline"
                      className="bg-yellow-50 text-yellow-700 border-yellow-300"
                    >
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      분석 중
                    </Badge>
                  )}
                  {record.analysis.status === "completed" && (
                    <Badge
                      variant="outline"
                      className="bg-green-50 text-green-700 border-green-300"
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      완료
                    </Badge>
                  )}
                  {record.analysis.status === "failed" && (
                    <Badge
                      variant="outline"
                      className="bg-red-50 text-red-700 border-red-300"
                    >
                      <XCircle className="h-3 w-3 mr-1" />
                      실패
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {record.analysis.message && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      메시지
                    </p>
                    <p className="text-sm">{record.analysis.message}</p>
                  </div>
                )}

                {record.analysis.completedAt && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      완료 시간
                    </p>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm">
                        {formatDate(record.analysis.completedAt)}
                      </p>
                    </div>
                  </div>
                )}

                {record.analysis.analysisResults &&
                  Object.keys(record.analysis.analysisResults).length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-3">
                        분석 컴포넌트 결과
                      </p>
                      <div className="space-y-4">
                        {Object.entries(record.analysis.analysisResults).map(
                          ([key, result]) => (
                            <Card key={key} className="bg-gray-50">
                              <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                  <CardTitle className="text-base">
                                    {result.componentType || key}
                                  </CardTitle>
                                  {getStatusBadge(result.status)}
                                </div>
                              </CardHeader>
                              <CardContent className="space-y-2">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="font-medium text-muted-foreground">
                                      PDF Key
                                    </p>
                                    <p className="break-all">{result.pdfKey}</p>
                                  </div>
                                  <div>
                                    <p className="font-medium text-muted-foreground">
                                      처리 시간
                                    </p>
                                    <p>{result.processingTimeMs}ms</p>
                                  </div>
                                </div>
                                {result.errorMessage && (
                                  <div>
                                    <p
                                      className={`font-medium ${
                                        result.status === "completed" ||
                                        result.status === "success"
                                          ? "text-green-600"
                                          : "text-red-600"
                                      }`}
                                    >
                                      {result.status === "completed" ||
                                      result.status === "success"
                                        ? "완료 메시지"
                                        : "오류 메시지"}
                                    </p>
                                    <p
                                      className={`text-sm ${
                                        result.status === "completed" ||
                                        result.status === "success"
                                          ? "text-green-600"
                                          : "text-red-600"
                                      }`}
                                    >
                                      {result.errorMessage}
                                    </p>
                                  </div>
                                )}
                                {result.analysisData &&
                                  Object.keys(result.analysisData).length >
                                    0 && (
                                    <div>
                                      <p className="font-medium text-muted-foreground mb-2">
                                        분석 데이터
                                      </p>
                                      <pre className="bg-white p-3 rounded border text-xs overflow-auto max-h-64">
                                        {JSON.stringify(
                                          result.analysisData,
                                          null,
                                          2
                                        )}
                                      </pre>
                                    </div>
                                  )}
                              </CardContent>
                            </Card>
                          )
                        )}
                      </div>
                    </div>
                  )}
              </CardContent>
            </Card>
          )}

          {!record.analysis && (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">
                  아직 분석이 시작되지 않았습니다. 상단의 "분석 요청" 버튼을
                  클릭하여 분석을 시작하세요.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* 사이드바 */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>요약</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  파일명
                </p>
                <p className="text-sm truncate">
                  {getFileNameFromBucketKey(record.bucketKey)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  분석 상태
                </p>
                {record.analysis ? (
                  <div className="mt-1">
                    {record.analysis.status === "processing" && (
                      <Badge
                        variant="outline"
                        className="bg-yellow-50 text-yellow-700 border-yellow-300"
                      >
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        분석 중
                      </Badge>
                    )}
                    {record.analysis.status === "completed" && (
                      <Badge
                        variant="outline"
                        className="bg-green-50 text-green-700 border-green-300"
                      >
                        완료
                      </Badge>
                    )}
                    {record.analysis.status === "failed" && (
                      <Badge
                        variant="outline"
                        className="bg-red-50 text-red-700 border-red-300"
                      >
                        실패
                      </Badge>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">-</p>
                )}
              </div>
              {record.analysis?.analysisResults && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    분석 컴포넌트 수
                  </p>
                  <p className="text-sm">
                    {Object.keys(record.analysis.analysisResults).length}개
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
